import { cva, type VariantProps } from 'class-variance-authority';

/**
 * Button Variants
 */
export const buttonVariants = cva(
  [
    // agn-btn is an inert hook: next-components.css (next scope only) drops
    // background and colour from the transition (handover-06 dark-mode class d).
    'agn-btn inline-flex items-center justify-center gap-2 whitespace-nowrap',
    'font-medium leading-none transition-colors',
    // verify-ds glyph-sizes: a flex row with gap shrinks its children by default once the
    // row's own content (label + icon) is wider than the button, compressing a declared
    // 24px icon to ~16px — the icon is still on the size scale in source, just crushed at
    // render. [&_svg]:shrink-0 opts every icon out of the flex-basis shrink so it renders
    // at its declared size regardless of label length.
    '[&_svg]:shrink-0',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-900',
    'disabled:pointer-events-none disabled:opacity-50',
    'touch-manipulation',
  ],
  {
    variants: {
      variant: {
        // Default/Primary - solid dark button (admin-ui uses 'default' for primary action)
        default: [
          'bg-slate-900 text-white',
          'hover:bg-slate-800',
          'focus-visible:ring-slate-900',
          'dark:bg-white dark:text-slate-900',
          'dark:hover:bg-slate-200',
          'dark:focus-visible:ring-white',
        ],
        primary: [
          'bg-slate-900 text-white',
          'hover:bg-slate-800',
          'focus-visible:ring-slate-900',
          'dark:bg-white dark:text-slate-900',
          'dark:hover:bg-slate-200',
          'dark:focus-visible:ring-white',
        ],
        // Outline - bordered button for secondary actions
        outline: [
          'bg-white text-slate-700 border border-slate-300',
          'hover:bg-slate-50',
          'focus-visible:ring-slate-500',
          'dark:bg-slate-800 dark:text-slate-100 dark:border-slate-600',
          'dark:hover:bg-slate-700',
        ],
        secondary: [
          'bg-white text-slate-700 border border-slate-300',
          'hover:bg-slate-50',
          'focus-visible:ring-slate-500',
          'dark:bg-slate-800 dark:text-slate-100 dark:border-slate-600',
          'dark:hover:bg-slate-700',
        ],
        ghost: [
          'bg-transparent text-slate-700',
          'hover:bg-slate-100',
          'focus-visible:ring-slate-500',
          'dark:text-slate-100',
          'dark:hover:bg-slate-700',
        ],
        // agn-btn--* are inert hooks: next-components.css (next scope only)
        // repaints the solid fills with the design's fill/-fg token pairs.
        destructive: [
          'agn-btn--destructive bg-rose-600 text-white',
          'hover:bg-rose-700',
          'focus-visible:ring-rose-600',
        ],
        success: [
          'agn-btn--success bg-emerald-600 text-white',
          'hover:bg-emerald-700',
          'focus-visible:ring-emerald-600',
        ],
        brand: [
          'agn-btn--brand bg-amber-500 text-white',
          'hover:bg-amber-600',
          'focus-visible:ring-amber-500',
        ],
        link: [
          'bg-transparent text-slate-900 underline-offset-4',
          'hover:underline',
          'focus-visible:ring-slate-500',
          'dark:text-slate-100',
        ],
      },
      size: {
        xs: 'h-7 px-2 text-xs rounded',
        sm: 'h-8 px-3 text-sm rounded-md',
        md: 'h-10 px-4 text-sm rounded-md',
        default: 'h-10 px-4 text-sm rounded-md', // Alias for md
        lg: 'h-11 px-6 text-base rounded-md',
        xl: 'h-12 px-8 text-base rounded-lg',
        icon: 'h-10 w-10 rounded-md',
        'icon-sm': 'h-8 w-8 rounded-md',
        'icon-lg': 'h-12 w-12 rounded-lg',
      },
      fullWidth: {
        true: 'w-full',
        false: '',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
      fullWidth: false,
    },
  }
);

export type ButtonVariants = VariantProps<typeof buttonVariants>;

/**
 * Input Variants
 */
export const inputVariants = cva(
  [
    'text-slate-900 placeholder-slate-400',
    'transition-all duration-150',
    'disabled:cursor-not-allowed disabled:opacity-50',
    'dark:text-white dark:placeholder-slate-500',
  ],
  {
    variants: {
      variant: {
        default: [
          'w-full bg-white border border-slate-200 rounded-md',
          'focus:outline-none focus:border-slate-400 focus:ring-1 focus:ring-slate-200',
          'disabled:bg-slate-50 dark:disabled:bg-slate-800',
          'dark:bg-slate-900 dark:border-slate-700',
          'dark:focus:border-slate-500 dark:focus:ring-slate-700',
        ],
        inline: [
          'bg-transparent border-none outline-none',
          'focus:outline-none focus:ring-0',
        ],
      },
      size: {
        sm: 'h-8 px-3 text-sm',
        md: 'h-10 px-3 text-sm',
        lg: 'h-12 px-4 text-base',
      },
      hasError: {
        true: [
          'border-rose-500 focus:ring-rose-500',
          'dark:border-rose-500 dark:focus:ring-rose-500',
        ],
        false: '',
      },
      hasIcon: {
        left: 'pl-10',
        right: 'pr-10',
        both: 'px-10',
        none: '',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'md',
      hasError: false,
      hasIcon: 'none',
    },
  }
);

export type InputVariants = VariantProps<typeof inputVariants>;

/**
 * Badge Variants
 */
export const badgeVariants = cva(
  [
    // The inline-container rail (design-of-record tokens/controls.css,
    // Badge.jsx.txt "ONE HEIGHT, NOT PADDING"; agion/Agion#3405): the size
    // declares the height, so the box never follows the font's line box.
    // leading-none sits after each text size: tailwind-merge drops a
    // leading-* that precedes a text-* size.
    'inline-flex items-center whitespace-nowrap box-border font-medium',
    'border rounded-md',
    'transition-colors',
    // verify-ds glyph-sizes: see buttonVariants — a gapped flex row shrinks an icon child
    // below its declared size once the row is tight; opt every icon out.
    '[&_svg]:shrink-0',
  ],
  {
    variants: {
      variant: {
        default: [
          'bg-slate-100 text-slate-700 border-slate-200',
          'dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700',
        ],
        secondary: [
          'bg-slate-50 text-slate-600 border-slate-200',
          'dark:bg-slate-900 dark:text-slate-400 dark:border-slate-700',
        ],
        outline: [
          'bg-transparent text-slate-700 border-slate-300',
          'dark:text-slate-300 dark:border-slate-600',
        ],
        muted: [
          'bg-slate-50 text-slate-500 border-transparent',
          'dark:bg-slate-800/50 dark:text-slate-400 dark:border-transparent',
        ],
        success: [
          'bg-emerald-50 text-emerald-700 border-emerald-200',
          'dark:bg-emerald-500/10 dark:text-emerald-300 dark:border-emerald-500/20',
        ],
        warning: [
          'bg-amber-50 text-amber-700 border-amber-200',
          'dark:bg-amber-500/10 dark:text-amber-300 dark:border-amber-500/20',
        ],
        error: [
          'bg-rose-50 text-rose-700 border-rose-200',
          'dark:bg-rose-500/10 dark:text-rose-300 dark:border-rose-500/20',
        ],
        destructive: [
          'bg-rose-50 text-rose-700 border-rose-200',
          'dark:bg-rose-500/10 dark:text-rose-300 dark:border-rose-500/20',
        ],
        info: [
          'bg-sky-50 text-sky-700 border-sky-200',
          'dark:bg-sky-500/10 dark:text-sky-300 dark:border-sky-500/20',
        ],
        brand: [
          'bg-amber-100 text-amber-800 border-amber-300',
          'dark:bg-amber-500/15 dark:text-amber-200 dark:border-amber-500/25',
        ],
        purple: [
          'bg-violet-50 text-violet-700 border-violet-200',
          'dark:bg-violet-500/10 dark:text-violet-300 dark:border-violet-500/20',
        ],
        premium: [
          'bg-gradient-to-r from-amber-100 to-yellow-100 text-amber-800 border-amber-300',
          'dark:from-amber-500/15 dark:to-yellow-500/15 dark:text-amber-200 dark:border-amber-500/25',
        ],
        cyan: [
          'bg-cyan-50 text-cyan-700 border-cyan-200',
          'dark:bg-cyan-500/10 dark:text-cyan-300 dark:border-cyan-500/20',
        ],
        indigo: [
          'bg-indigo-50 text-indigo-700 border-indigo-200',
          'dark:bg-indigo-500/10 dark:text-indigo-300 dark:border-indigo-500/20',
        ],
        orange: [
          'bg-orange-50 text-orange-700 border-orange-200',
          'dark:bg-orange-500/10 dark:text-orange-300 dark:border-orange-500/20',
        ],
        pink: [
          'bg-pink-50 text-pink-700 border-pink-200',
          'dark:bg-pink-500/10 dark:text-pink-300 dark:border-pink-500/20',
        ],
        blue: [
          'bg-blue-50 text-blue-700 border-blue-200',
          'dark:bg-blue-500/10 dark:text-blue-300 dark:border-blue-500/20',
        ],
      },
      shape: {
        default: '',
        pill: 'rounded-full',
        // A number: a pill at least as wide as it is tall, centred tabular figures.
        count: 'rounded-full justify-center tabular-nums',
      },
      size: {
        sm: 'h-[var(--inline-h,24px)] px-2 text-xs leading-none',
        md: 'h-[var(--inline-h,24px)] px-2.5 text-xs leading-none',
        lg: 'h-[var(--inline-h-lg,28px)] px-3 text-sm leading-none',
      },
    },
    compoundVariants: [
      { shape: 'count', size: ['sm', 'md'], className: 'min-w-[var(--inline-h,24px)] px-1.5' },
      { shape: 'count', size: 'lg', className: 'min-w-[var(--inline-h-lg,28px)] px-1.5' },
    ],
    defaultVariants: {
      variant: 'default',
      shape: 'default',
      size: 'md',
    },
  }
);

export type BadgeVariants = VariantProps<typeof badgeVariants>;

/**
 * Card Variants
 */
export const cardVariants = cva(
  [
    'bg-white border border-slate-200 rounded-lg',
    'dark:bg-slate-800 dark:border-slate-700',
  ],
  {
    variants: {
      variant: {
        default: 'shadow-sm',
        elevated: 'shadow-md',
        outline: 'shadow-none',
        ghost: 'border-transparent shadow-none bg-transparent',
      },
      interactive: {
        true: [
          'cursor-pointer transition-all',
          'hover:shadow-md hover:scale-[1.01]',
          'active:scale-[0.99]',
        ],
        false: '',
      },
      padding: {
        none: 'p-0',
        sm: 'p-3',
        md: 'p-4',
        lg: 'p-6',
      },
    },
    defaultVariants: {
      variant: 'default',
      interactive: false,
      padding: 'md',
    },
  }
);

export type CardVariants = VariantProps<typeof cardVariants>;

/**
 * Alert Variants
 */
export const alertVariants = cva(
  [
    'relative w-full rounded-lg border p-4 flex items-center gap-3',
    '[&>svg]:shrink-0',
  ],
  {
    variants: {
      variant: {
        default: [
          'bg-slate-50 border-slate-200 text-slate-900',
          'dark:bg-slate-800 dark:border-slate-600 dark:text-slate-100',
        ],
        success: [
          'bg-emerald-50 border-emerald-200 text-emerald-900',
          'dark:bg-emerald-500/10 dark:border-emerald-500/20 dark:text-emerald-100',
          '[&>svg]:text-emerald-600 dark:[&>svg]:text-emerald-400',
        ],
        warning: [
          'bg-amber-50 border-amber-200 text-amber-900',
          'dark:bg-amber-500/10 dark:border-amber-500/20 dark:text-amber-100',
          '[&>svg]:text-amber-600 dark:[&>svg]:text-amber-400',
        ],
        error: [
          'bg-rose-50 border-rose-200 text-rose-900',
          'dark:bg-rose-500/10 dark:border-rose-500/20 dark:text-rose-100',
          '[&>svg]:text-rose-600 dark:[&>svg]:text-rose-400',
        ],
        info: [
          'bg-sky-50 border-sky-200 text-sky-900',
          'dark:bg-sky-500/10 dark:border-sky-500/20 dark:text-sky-100',
          '[&>svg]:text-sky-600 dark:[&>svg]:text-sky-400',
        ],
        purple: [
          'bg-violet-50 border-violet-200 text-violet-900',
          'dark:bg-violet-500/10 dark:border-violet-500/20 dark:text-violet-100',
          '[&>svg]:text-violet-600 dark:[&>svg]:text-violet-400',
        ],
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

export type AlertVariants = VariantProps<typeof alertVariants>;

/**
 * Chat Message Variants
 */
export const chatMessageVariants = cva(
  [
    'rounded-2xl px-4 py-3 max-w-[85%]',
    'break-words leading-relaxed',
  ],
  {
    variants: {
      role: {
        user: [
          'bg-slate-900 text-white ml-auto',
          'rounded-br-sm',
          'dark:bg-sky-600 dark:text-white',
        ],
        assistant: [
          'bg-white text-slate-900 mr-auto',
          'border border-slate-200 shadow-sm',
          'rounded-bl-sm',
          'dark:bg-slate-800 dark:text-slate-100 dark:border-slate-700',
        ],
        system: [
          'bg-slate-50 text-slate-600 mx-auto text-center text-sm',
          'border border-slate-200 rounded-lg',
          'dark:bg-slate-900 dark:text-slate-400 dark:border-slate-800',
        ],
      },
      size: {
        sm: 'text-sm px-3 py-2',
        md: 'text-base',
        lg: 'text-lg px-5 py-4',
      },
    },
    defaultVariants: {
      role: 'assistant',
      size: 'md',
    },
  }
);

export type ChatMessageVariants = VariantProps<typeof chatMessageVariants>;

/**
 * Role Badge Variants
 *
 * Unified role badge styling for user roles in the Agion platform.
 * Color scheme based on Agion brand palette:
 * - Owner: Amber (brand color) - highest authority
 * - Admin: Violet - administrative privileges
 * - Manager: Sky - team management
 * - User: Slate - standard user
 * - Auditor: Emerald - compliance/audit role
 */
export const roleBadgeVariants = cva(
  [
    // On the inline-container rail like Badge (agion/Agion#3405).
    'inline-flex items-center whitespace-nowrap box-border gap-1 font-medium',
    'rounded-md',
    'transition-colors',
    // verify-ds glyph-sizes: see buttonVariants.
    '[&_svg]:shrink-0',
  ],
  {
    variants: {
      role: {
        owner: [
          'bg-amber-50 text-amber-800 border border-amber-200',
          'dark:bg-amber-500/10 dark:text-amber-300 dark:border-amber-500/20',
        ],
        admin: [
          'bg-violet-50 text-violet-800 border border-violet-200',
          'dark:bg-violet-500/10 dark:text-violet-300 dark:border-violet-500/20',
        ],
        manager: [
          'bg-sky-50 text-sky-800 border border-sky-200',
          'dark:bg-sky-500/10 dark:text-sky-300 dark:border-sky-500/20',
        ],
        user: [
          'bg-slate-100 text-slate-700 border border-slate-200',
          'dark:bg-slate-400/10 dark:text-slate-300 dark:border-slate-400/20',
        ],
        auditor: [
          'bg-emerald-50 text-emerald-800 border border-emerald-200',
          'dark:bg-emerald-500/10 dark:text-emerald-300 dark:border-emerald-500/20',
        ],
        // Guest: an external member (ADR-20260930). A dashed outline marks
        // the person as outside the organisation without implying rights.
        guest: [
          'bg-transparent text-slate-700 border border-dashed border-slate-400',
          'dark:text-slate-300 dark:border-slate-500',
        ],
      },
      emphasis: {
        default: '',
        strong: '',
      },
      size: {
        // One box for both sizes; md only carries larger text.
        sm: 'h-[var(--inline-h,24px)] px-2 text-xs leading-none',
        md: 'h-[var(--inline-h,24px)] px-2.5 text-sm leading-none',
      },
    },
    compoundVariants: [
      // Strong emphasis variants - slightly stronger tint
      {
        role: 'owner',
        emphasis: 'strong',
        className: 'bg-amber-100 text-amber-900 border-amber-300 dark:bg-amber-500/15 dark:text-amber-200 dark:border-amber-500/30',
      },
      {
        role: 'admin',
        emphasis: 'strong',
        className: 'bg-violet-100 text-violet-900 border-violet-300 dark:bg-violet-500/15 dark:text-violet-200 dark:border-violet-500/30',
      },
      {
        role: 'manager',
        emphasis: 'strong',
        className: 'bg-sky-100 text-sky-900 border-sky-300 dark:bg-sky-500/15 dark:text-sky-200 dark:border-sky-500/30',
      },
      {
        role: 'user',
        emphasis: 'strong',
        className: 'bg-slate-200 text-slate-800 border-slate-300 dark:bg-slate-400/15 dark:text-slate-200 dark:border-slate-400/30',
      },
      {
        role: 'auditor',
        emphasis: 'strong',
        className: 'bg-emerald-100 text-emerald-900 border-emerald-300 dark:bg-emerald-500/15 dark:text-emerald-200 dark:border-emerald-500/30',
      },
      {
        role: 'guest',
        emphasis: 'strong',
        className: 'text-slate-800 border-slate-500 dark:text-slate-200 dark:border-slate-400',
      },
    ],
    defaultVariants: {
      role: 'user',
      emphasis: 'default',
      size: 'sm',
    },
  }
);

export type RoleBadgeVariants = VariantProps<typeof roleBadgeVariants>;

/** System role type for role badge */
export type SystemRole = 'owner' | 'admin' | 'manager' | 'user' | 'auditor' | 'guest';

/**
 * Progress Variants
 */
export const progressVariants = cva(
  [
    'relative w-full overflow-hidden rounded-full',
    'bg-slate-200 dark:bg-slate-700',
  ],
  {
    variants: {
      size: {
        sm: 'h-1.5',
        md: 'h-2.5',
        lg: 'h-4',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  }
);

export const progressIndicatorVariants = cva(
  [
    'h-full w-full flex-1 transition-all',
  ],
  {
    variants: {
      variant: {
        default: [
          'bg-slate-900',
          'dark:bg-slate-100',
        ],
        success: [
          'bg-emerald-600',
          'dark:bg-emerald-500',
        ],
        warning: [
          'bg-amber-500',
          'dark:bg-amber-400',
        ],
        error: [
          'bg-rose-600',
          'dark:bg-rose-500',
        ],
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

export type ProgressVariants = VariantProps<typeof progressVariants>;
export type ProgressIndicatorVariants = VariantProps<typeof progressIndicatorVariants>;

/**
 * Trust Badge Variants
 *
 * Unified trust tier indicator styling for AI governance.
 * Uses the Agion brand color palette with semantic meaning:
 * - Tier 0 (Restricted): Rose - Minimal trust, high restrictions
 * - Tier 1 (Limited): Amber - Some trust, some restrictions
 * - Tier 2 (Standard): Emerald - Normal trust level
 * - Tier 3 (Elevated): Deep green - High trust, extended permissions
 */
// The TrustBadge component no longer reads these classes (it is the record
// twin, src/next/TrustBadge); they stay exported for callers that style a
// trust chip themselves, on the same rail.
export const trustBadgeVariants = cva(
  [
    'inline-flex items-center whitespace-nowrap box-border gap-1.5 font-medium',
    'border rounded-md',
    'transition-colors',
    // verify-ds glyph-sizes: see buttonVariants.
    '[&_svg]:shrink-0',
  ],
  {
    variants: {
      tier: {
        0: [
          'bg-rose-50 text-rose-800 border-rose-200/70',
          'dark:bg-rose-500/10 dark:text-rose-300 dark:border-rose-500/20',
        ],
        1: [
          'bg-amber-50 text-amber-800 border-amber-200/70',
          'dark:bg-amber-500/10 dark:text-amber-300 dark:border-amber-500/20',
        ],
        2: [
          'bg-emerald-50 text-emerald-700 border-emerald-200/70',
          'dark:bg-emerald-500/10 dark:text-emerald-300 dark:border-emerald-500/20',
        ],
        3: [
          'bg-green-100 text-green-900 border-green-300/80',
          'dark:bg-green-500/10 dark:text-green-300 dark:border-green-500/20',
        ],
      },
      size: {
        sm: 'h-[var(--inline-h,24px)] px-2 text-xs leading-none',
        md: 'h-[var(--inline-h,24px)] px-2.5 text-xs leading-none',
        lg: 'h-[var(--inline-h-lg,28px)] px-3 text-sm leading-none',
      },
    },
    defaultVariants: {
      tier: 2,
      size: 'md',
    },
  }
);

export type TrustBadgeVariants = VariantProps<typeof trustBadgeVariants>;

/** Trust tier type (0-3) */
export type TrustTier = 0 | 1 | 2 | 3;

/**
 * Status Variants
 *
 * Operational status indicators with consistent premium styling
 * matching the badge system.
 */
export const statusVariants = cva(
  [
    'inline-flex items-center gap-2 rounded-md text-xs font-medium',
    // verify-ds glyph-sizes: see buttonVariants.
    '[&_svg]:shrink-0',
  ],
  {
    variants: {
      status: {
        online: [
          'bg-emerald-50 text-emerald-800 border border-emerald-200',
          'dark:bg-emerald-500/10 dark:text-emerald-300 dark:border-emerald-500/20',
        ],
        offline: [
          'bg-slate-100 text-slate-600 border border-slate-200',
          'dark:bg-slate-400/10 dark:text-slate-400 dark:border-slate-400/20',
        ],
        warning: [
          'bg-amber-50 text-amber-800 border border-amber-200',
          'dark:bg-amber-500/10 dark:text-amber-300 dark:border-amber-500/20',
        ],
        error: [
          'bg-rose-50 text-rose-800 border border-rose-200',
          'dark:bg-rose-500/10 dark:text-rose-300 dark:border-rose-500/20',
        ],
        maintenance: [
          'bg-sky-50 text-sky-800 border border-sky-200',
          'dark:bg-sky-500/10 dark:text-sky-300 dark:border-sky-500/20',
        ],
        pending: [
          'bg-amber-50 text-amber-800 border border-amber-200',
          'dark:bg-amber-500/10 dark:text-amber-300 dark:border-amber-500/20',
        ],
        inactive: [
          'bg-slate-50 text-slate-500 border border-slate-100',
          'dark:bg-slate-400/10 dark:text-slate-400 dark:border-slate-400/15',
        ],
      },
      size: {
        sm: 'px-2 py-0.5 text-xs',
        md: 'px-2.5 py-0.5 text-xs',
        lg: 'px-3 py-1 text-sm',
      },
    },
    defaultVariants: {
      status: 'offline',
      size: 'md',
    },
  }
);

export type StatusVariants = VariantProps<typeof statusVariants>;
